<template>
	<section
		class="chat-window"
		:class="{ 'chat-window--minimized': popup.minimized }"
		role="dialog"
		:aria-label="title"
	>
		<div
			v-if="popup.minimized"
			class="chat-window__bar"
			:class="{ 'chat-window__bar--unread': unreadCount > 0 }"
			role="button"
			tabindex="0"
			:aria-label="barLabel"
			@click="restore"
			@keydown.enter.self.prevent="restore"
			@keydown.space.self.prevent="restore"
		>
			<channel-avatar :type-id="popup.typeId" :image="counterpart.image" :size="36" badge />
			<div class="chat-window__heading">
				<span class="chat-window__title">{{ title }}</span>
				<span v-if="subtitle" class="chat-window__subtitle">{{ subtitle }}</span>
			</div>
			<span v-if="unreadCount > 0" class="chat-window__bar-badge">{{ unreadLabel }}</span>
			<button type="button" class="chat-window__icon-button" :aria-label="$t('chat.close')" @click.stop="close">
				<chat-icon name="close" :size="18" />
			</button>
		</div>

		<template v-else>
			<header class="chat-window__header">
				<channel-avatar :type-id="popup.typeId" :image="counterpart.image" :size="40" badge />
				<div class="chat-window__heading">
					<span class="chat-window__title">{{ title }}</span>
					<span v-if="subtitle" class="chat-window__subtitle">{{ subtitle }}</span>
				</div>
				<button
					type="button"
					class="chat-window__icon-button"
					:aria-label="$t('chat.minimize')"
					:title="$t('chat.minimize')"
					@click="minimize"
				>
					<chat-icon name="minimize" :size="18" />
				</button>
				<button
					type="button"
					class="chat-window__icon-button"
					:aria-label="$t('chat.close')"
					:title="$t('chat.close')"
					@click="close"
				>
					<chat-icon name="close" :size="18" />
				</button>
			</header>

			<div class="chat-window__info">
				<router-link v-if="showOrderLink" class="chat-window__order-link" :to="orderRoute" @click="onOrderLink">
					<chat-icon name="receipt" :size="14" />
					<span class="chat-window__order-link-label">{{ $t('chat.order_view') }}</span>
					<chat-icon name="chevron-right" :size="16" />
				</router-link>
				<span v-if="thread && !orderCode" class="chat-window__general">{{ $t('chat.general_chat') }}</span>
				<button
					v-if="thread && thread.open"
					type="button"
					class="chat-window__end"
					:disabled="closing"
					@click="confirmingEnd = !confirmingEnd"
				>
					<app-icon v-if="closing" icon="spin-loading" />
					{{ $t('chat.close_action') }}
				</button>
			</div>

			<div v-if="confirmingEnd" class="chat-window__confirm" role="alertdialog">
				<p>{{ $t('chat.close_confirm') }}</p>
				<div class="chat-window__confirm-actions">
					<button type="button" class="chat-window__ghost" @click="confirmingEnd = false">
						{{ $t('chat.cancel') }}
					</button>
					<button type="button" class="chat-window__danger" @click="endChat">
						{{ $t('chat.close_action') }}
					</button>
				</div>
			</div>

			<div ref="list" class="chat-window__messages">
				<div v-if="!thread" class="chat-window__loading">
					<app-icon icon="spin-loading" />
				</div>
				<template v-else>
					<p v-if="!items.length" class="chat-window__empty">{{ $t('chat.empty') }}</p>
					<template v-for="item in items" :key="item.key">
						<div v-if="item.day" class="chat-window__day">
							<span>{{ item.day }}</span>
						</div>
						<div v-else class="chat-bubble-row" :class="{ 'chat-bubble-row--mine': item.mine }">
							<channel-avatar
								v-if="!item.mine"
								:type-id="item.senderTypeId"
								:image="item.image"
								:size="28"
								class="chat-bubble-row__avatar"
							/>
							<div
								class="chat-bubble"
								:class="item.mine ? 'chat-bubble--mine' : 'chat-bubble--theirs'"
								:style="item.mine ? null : { background: item.tint }"
							>
								<span v-if="!item.mine" class="chat-bubble__name" :style="{ color: item.accent }">
									{{ item.name }}
								</span>
								<button
									v-if="item.photo"
									type="button"
									class="chat-bubble__photo"
									:aria-label="$t('chat.photo_open')"
									@click="viewerUrl = item.photo"
								>
									<img :src="item.photo" alt="" loading="lazy" />
								</button>
								<p v-if="item.text" class="chat-bubble__text">{{ item.text }}</p>
								<span class="chat-bubble__meta">
									<time :datetime="item.date">{{ item.time }}</time>
									<chat-icon
										v-if="item.mine"
										:name="item.read ? 'done-all' : 'check'"
										:size="14"
										:class="{ 'chat-bubble__tick--read': item.read }"
									/>
								</span>
							</div>
						</div>
					</template>
					<div v-if="thread.othersTyping" class="chat-bubble-row" :aria-label="$t('chat.typing')">
						<channel-avatar
							:type-id="popup.typeId"
							:image="counterpart.image"
							:size="28"
							class="chat-bubble-row__avatar"
						/>
						<div
							class="chat-bubble chat-bubble--theirs chat-bubble--typing"
							:style="{ background: typingTint }"
						>
							<i v-for="dot in 3" :key="dot" class="chat-bubble__dot" />
						</div>
					</div>
				</template>
			</div>

			<chat-composer
				ref="composer"
				v-model:draft="draft"
				:pending-image="pendingImage"
				:can-compose="canCompose"
				:sending="sending"
				:preparing-photo="preparingPhoto"
				:send-disabled="sendDisabled"
				:max-length="maxLength"
				@input="onInput"
				@send="send"
				@attach="attachPhoto"
				@remove-image="pendingImage = null"
				@open-photo="viewerUrl = $event"
			/>
		</template>

		<chat-image-viewer ref="viewer" :url="viewerUrl" @close="viewerUrl = null" />
	</section>
</template>

<script>
	import { markRaw } from 'vue';
	import { mapGetters } from 'vuex';
	import {
		CHAT_TYPE,
		GENERAL_ORDER_CODE,
		channelNameOf,
		chatChannelLabelKey,
		chatDisplayName,
		chatTypeIdOfSender,
		unreadBadgeLabel,
	} from '@/services/chat/channels';
	import { closeChat, createChat, sendChatMessage } from '@/services/chat/chatApi';
	import {
		findOpenChatId,
		isMine,
		isUnreadIncoming,
		markChatRead,
		setChatTyping,
		watchChat,
	} from '@/services/chat/chatFirestore';
	import {
		askToRateChat,
		blurPopup,
		chatStore,
		closePopup,
		findOpenChat,
		focusPopup,
		hasSeveralRestaurants,
		minimizePopup,
		resolvePopupChat,
		restorePopup,
	} from '@/services/chat/chatStore';
	import { resizeImage } from '@/services/chat/imageResize';
	import UtilityService from '@/services/UtilityService';
	import RouteNames from '@/router/names';
	import ChannelAvatar from './ChannelAvatar.vue';
	import ChatComposer from './ChatComposer.vue';
	import ChatIcon from './ChatIcon.vue';
	import ChatImageViewer from './ChatImageViewer.vue';
	import { chatChannelVisual } from './chatVisuals';
	import { showChatToast } from './chatToasts';

	const LOOKUP_RETRY_MS = 1500;
	const CLOSE_SETTLE_MS = 1500;
	const TYPING_IDLE_MS = 3000;
	const MAX_MESSAGE_LENGTH = 500;
	const NEAR_BOTTOM_PX = 48;
	const NOBODY = { name: '', image: '' };
	const ENDED_KEYS = {
		[CHAT_TYPE.CUSTOMER]: 'chat.ended_customer',
		[CHAT_TYPE.SUPPORT]: 'chat.ended_support',
		[CHAT_TYPE.DRIVER]: 'chat.ended_driver',
	};

	const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

	export default {
		name: 'ChatWindow',
		components: { ChannelAvatar, ChatComposer, ChatIcon, ChatImageViewer },
		props: {
			popup: {
				type: Object,
				required: true,
			},
		},
		data: () => ({
			thread: null,
			draft: '',
			pendingImage: null,
			preparingPhoto: false,
			sending: false,
			closing: false,
			confirmingEnd: false,
			viewerUrl: null,
			tabVisible: document.visibilityState === 'visible',
			maxLength: MAX_MESSAGE_LENGTH,
		}),
		computed: {
			...mapGetters({
				user: 'user',
			}),
			focused() {
				return chatStore.focusedPopupKey === this.popup.key;
			},
			listed() {
				const { typeId, chatId, chatKey, orderCode } = this.popup;
				return findOpenChat({ typeId, chatId, key: chatKey, orderCode });
			},
			counterpart() {
				if (this.thread) {
					return this.thread.counterpart;
				}
				return this.listed ? this.listed.counterpart : NOBODY;
			},
			title() {
				return chatDisplayName(this.$t, this.popup.typeId, this.counterpart);
			},
			channelLabel() {
				return this.$t(chatChannelLabelKey(this.popup.typeId));
			},
			businessName() {
				if (!hasSeveralRestaurants(this.user)) {
					return '';
				}
				const source = this.thread || this.listed;
				return source ? source.businessName : '';
			},
			subtitle() {
				return [this.title === this.channelLabel ? '' : this.channelLabel, this.businessName]
					.filter(Boolean)
					.join(' · ');
			},
			orderCode() {
				const code = this.thread ? this.thread.orderCode : '';
				return code && code !== GENERAL_ORDER_CODE ? code : '';
			},
			// The details route carries an encrypted order id; the backend takes the order code there too.
			orderRoute() {
				return { name: RouteNames.OrderDetails, params: { id: UtilityService.encrypt(this.orderCode) } };
			},
			// A details page opened from a list names its order by id, so it reports the code once loaded.
			onOrderPage() {
				const { name, params } = this.$route;
				return (
					chatStore.viewedOrderCode === this.orderCode ||
					(name === RouteNames.OrderDetails && params.id === this.orderRoute.params.id)
				);
			},
			showOrderLink() {
				return !!this.orderCode && !this.onOrderPage;
			},
			unreadCount() {
				return this.thread ? this.thread.messages.filter(isUnreadIncoming).length : 0;
			},
			unreadLabel() {
				return unreadBadgeLabel(this.unreadCount);
			},
			barLabel() {
				return [
					this.title,
					this.subtitle,
					this.unreadCount > 0 ? this.$t('chat.list_unread', { count: this.unreadCount }) : null,
				]
					.filter(Boolean)
					.join(', ');
			},
			canCompose() {
				return !!this.thread && this.thread.open && !!this.thread.key && !this.closing;
			},
			sendDisabled() {
				return (
					!this.canCompose ||
					this.sending ||
					this.preparingPhoto ||
					(!this.draft.trim() && !this.pendingImage)
				);
			},
			readable() {
				return !this.popup.minimized && this.tabVisible && !!this.thread && this.thread.open;
			},
			items() {
				if (!this.thread) {
					return [];
				}
				const { typeId } = this.popup;
				const counterpart = this.thread.counterpart;
				const items = [];
				let day = null;
				this.thread.messages.forEach((message) => {
					const dayKey = UtilityService.chatDayKey(message.date);
					if (dayKey && dayKey !== day) {
						day = dayKey;
						items.push({ key: `day:${dayKey}`, day: UtilityService.chatDayLabel(message.date) });
					}
					const mine = isMine(message);
					const senderTypeId = chatTypeIdOfSender(message.entityType);
					const fromCounterpart = typeId !== CHAT_TYPE.SUPPORT && senderTypeId === typeId;
					items.push({
						key: message.id,
						mine,
						text: message.text,
						date: message.date,
						time: UtilityService.chatClockTime(message.date),
						read: message.readStatus === 1,
						photo: message.imageUrl.startsWith('http') ? message.imageUrl : '',
						senderTypeId,
						accent: chatChannelVisual(senderTypeId).accent,
						tint: chatChannelVisual(senderTypeId).tint,
						name:
							message.entityName.trim() ||
							(fromCounterpart ? counterpart.name : '') ||
							this.$t(chatChannelLabelKey(senderTypeId)),
						image: message.entityImage || (fromCounterpart ? counterpart.image : ''),
					});
				});
				return items;
			},
			othersTyping() {
				return !!this.thread && this.thread.othersTyping;
			},
			typingTint() {
				return chatChannelVisual(this.popup.typeId).tint;
			},
		},
		watch: {
			'popup.chatId'(chatId) {
				if (chatId) {
					this.listen(chatId);
				}
			},
			listed(listed) {
				if (listed && !this.popup.chatId) {
					resolvePopupChat(this.popup.key, listed.chatId);
				}
			},
			'popup.minimized'(minimized) {
				if (!minimized) {
					this.$nextTick(() => {
						this.scrollToBottom();
						this.focusInput();
					});
				}
			},
			readable() {
				this.markRead();
			},
			thread(thread, previous) {
				this.markRead();
				if (thread && !previous) {
					this.$nextTick(() => {
						if (this.focused) {
							this.focusInput();
						}
					});
				}
			},
			items() {
				this.keepAtBottom();
			},
			othersTyping() {
				this.keepAtBottom();
			},
		},
		methods: {
			resolveTarget() {
				if (this.popup.chatId) {
					this.listen(this.popup.chatId);
				} else if (this.listed) {
					resolvePopupChat(this.popup.key, this.listed.chatId);
				} else if (this.popup.chatKey) {
					this.lookUp();
				} else if (this.popup.orderCode != null) {
					this.create();
				}
			},
			async lookUp() {
				const { key, typeId, chatKey } = this.popup;
				const find = () =>
					findOpenChatId({ businessCodes: chatStore.businessCodes, typeId, key: chatKey }).catch(() => null);
				let found = await find();
				if (!found && this.alive && !this.popup.chatId) {
					await sleep(LOOKUP_RETRY_MS);
					if (this.alive && !this.popup.chatId) {
						found = await find();
					}
				}
				if (!this.alive || this.popup.chatId) {
					return;
				}
				if (found) {
					resolvePopupChat(key, found);
				} else {
					closePopup(key);
					showChatToast({ kind: 'info', text: this.$t('chat.no_longer_open') });
				}
			},
			async create() {
				const { key, typeId, orderCode } = this.popup;
				try {
					const chatId = await createChat({ channelName: channelNameOf(typeId), orderCode });
					if (this.alive && !this.popup.chatId) {
						resolvePopupChat(key, chatId);
					}
				} catch (error) {
					if (this.alive && !this.popup.chatId) {
						closePopup(key);
						showChatToast({ kind: 'error', text: UtilityService.extractErrorMessage(error) });
					}
				}
			},
			listen(chatId) {
				this.stopListening();
				this.stopWatch = watchChat({
					typeId: this.popup.typeId,
					chatId,
					businessCodes: chatStore.businessCodes,
					onChange: (thread) => {
						if (thread) {
							this.thread = markRaw(thread);
						}
						if (thread && thread.open) {
							this.seenOpen = true;
						} else {
							this.onNotOpen(!thread);
						}
					},
					onError: () => this.onNotOpen(true),
				});
			},
			stopListening() {
				if (this.stopWatch) {
					this.stopWatch();
					this.stopWatch = null;
				}
			},
			onNotOpen(gone) {
				if (!this.closing) {
					this.ended();
				} else if (gone) {
					this.goneWhileClosing = true;
				}
			},
			ended() {
				if (this.over) {
					return;
				}
				this.over = true;
				this.stopTyping();
				this.stopListening();
				const { name } = this.counterpart;
				const { thread } = this;
				let text = this.$t('chat.no_longer_open');
				if (this.seenOpen) {
					text = name
						? this.$t('chat.ended', { name, interpolation: { escapeValue: false } })
						: this.$t(ENDED_KEYS[this.popup.typeId] || ENDED_KEYS[CHAT_TYPE.SUPPORT]);
				}
				closePopup(this.popup.key);
				showChatToast({ kind: 'info', text });
				// Closed while this window saw it open, not deleted or refused.
				if (this.seenOpen && thread && !thread.open) {
					askToRateChat({ typeId: thread.typeId, chatId: thread.id });
				}
			},
			markRead() {
				if (!this.readable) {
					return;
				}
				const { thread } = this;
				const messageIds = thread.messages
					.filter((message) => isUnreadIncoming(message) && !this.readRequested.has(message.id))
					.map((message) => message.id);
				if (messageIds.length === 0) {
					return;
				}
				messageIds.forEach((id) => this.readRequested.add(id));
				markChatRead({ typeId: thread.typeId, chatId: thread.id, messageIds });
			},
			writeTyping(typing) {
				const { thread } = this;
				if (thread && thread.open && thread.id) {
					setChatTyping({
						typeId: thread.typeId,
						chatId: thread.id,
						businessCode: thread.businessCode,
						typing,
					});
				}
			},
			stopTyping() {
				clearTimeout(this.typingTimer);
				this.typingTimer = null;
				if (this.typingOn) {
					this.typingOn = false;
					this.writeTyping(false);
				}
			},
			onInput() {
				if (!this.thread || !this.thread.open) {
					return;
				}
				if (this.draft.length > 0 && !this.typingOn) {
					this.typingOn = true;
					this.writeTyping(true);
				}
				clearTimeout(this.typingTimer);
				this.typingTimer = setTimeout(() => this.stopTyping(), TYPING_IDLE_MS);
			},
			async send() {
				const text = this.draft.trim();
				const image = this.pendingImage;
				const { thread } = this;
				if ((!text && !image) || this.sendDisabled) {
					return;
				}
				this.sending = true;
				this.stickToBottom = true;
				this.draft = '';
				this.pendingImage = null;
				this.stopTyping();
				this.$nextTick(this.autosize);
				try {
					await sendChatMessage({
						channelName: channelNameOf(thread.typeId),
						chatId: thread.id,
						text,
						imageDataUrl: image ? image.dataUrl : undefined,
					});
				} catch (error) {
					if (this.alive) {
						this.draft = this.draft ? `${text}\n${this.draft}` : text;
						if (image && !this.pendingImage) {
							this.pendingImage = image;
						}
						this.$nextTick(this.autosize);
					}
					showChatToast({ kind: 'error', text: UtilityService.extractErrorMessage(error) });
				} finally {
					if (this.alive) {
						this.sending = false;
					}
				}
			},
			async endChat() {
				const { thread } = this;
				this.confirmingEnd = false;
				if (!thread || !thread.open || this.closing) {
					return;
				}
				this.closing = true;
				this.goneWhileClosing = false;
				this.stopTyping();
				try {
					await closeChat({ channelName: channelNameOf(thread.typeId), chatId: thread.id });
					if (this.alive) {
						this.over = true;
						closePopup(this.popup.key);
						askToRateChat({ typeId: thread.typeId, chatId: thread.id });
					}
				} catch (error) {
					if (!this.alive) {
						return;
					}
					this.closing = false;
					if (this.goneWhileClosing) {
						this.ended();
						return;
					}
					showChatToast({ kind: 'error', text: this.$t('chat.close_failed') });
					setTimeout(() => {
						if (this.alive && !this.closing && this.thread && !this.thread.open) {
							this.ended();
						}
					}, CLOSE_SETTLE_MS);
				}
			},
			async attachPhoto(file) {
				if (!this.canCompose || this.preparingPhoto) {
					return;
				}
				this.preparingPhoto = true;
				try {
					const { dataUrl } = await resizeImage(file);
					if (this.alive) {
						this.pendingImage = { dataUrl };
					}
				} catch (error) {
					showChatToast({ kind: 'error', text: error.message || this.$t('chat.photo_error') });
				} finally {
					if (this.alive) {
						this.preparingPhoto = false;
					}
				}
			},
			onOrderLink() {
				if (UtilityService.isMobile()) {
					minimizePopup(this.popup.key);
				}
			},
			minimize() {
				this.confirmingEnd = false;
				minimizePopup(this.popup.key);
			},
			restore() {
				restorePopup(this.popup.key);
			},
			close() {
				closePopup(this.popup.key);
			},
			autosize() {
				if (this.$refs.composer) {
					this.$refs.composer.autosize();
				}
			},
			focusInput() {
				if (this.$refs.composer) {
					this.$refs.composer.focus();
				}
			},
			isNearBottom() {
				const list = this.$refs.list;
				return !list || list.scrollHeight - list.scrollTop - list.clientHeight < NEAR_BOTTOM_PX;
			},
			keepAtBottom() {
				if (this.isNearBottom() || this.stickToBottom) {
					this.stickToBottom = false;
					this.$nextTick(this.scrollToBottom);
				}
			},
			scrollToBottom() {
				const list = this.$refs.list;
				if (list) {
					list.scrollTop = list.scrollHeight;
				}
			},
			onDocumentPointer(event) {
				const inside =
					this.$el.contains(event.target) ||
					(!!this.$refs.viewer &&
						!!this.$refs.viewer.$refs.root &&
						this.$refs.viewer.$refs.root.contains(event.target));
				if (inside) {
					focusPopup(this.popup.key);
				} else {
					blurPopup(this.popup.key);
				}
			},
			onVisibilityChange() {
				this.tabVisible = document.visibilityState === 'visible';
			},
			onPageHide() {
				this.stopTyping();
			},
		},
		created() {
			this.alive = true;
			this.over = false;
			this.seenOpen = false;
			this.goneWhileClosing = false;
			this.stickToBottom = false;
			this.typingOn = false;
			this.typingTimer = null;
			this.readRequested = new Set();
			this.resolveTarget();
		},
		mounted() {
			document.addEventListener('mousedown', this.onDocumentPointer, true);
			document.addEventListener('focusin', this.onDocumentPointer, true);
			document.addEventListener('visibilitychange', this.onVisibilityChange);
			window.addEventListener('pagehide', this.onPageHide);
		},
		beforeUnmount() {
			this.alive = false;
			this.stopTyping();
			this.stopListening();
			document.removeEventListener('mousedown', this.onDocumentPointer, true);
			document.removeEventListener('focusin', this.onDocumentPointer, true);
			document.removeEventListener('visibilitychange', this.onVisibilityChange);
			window.removeEventListener('pagehide', this.onPageHide);
		},
	};
</script>

<style lang="scss" scoped>
	$chat-green: #08cc04;
	$chat-green-dark: #037800;
	$ink: #1a202c;
	$text: #4a5568;
	$muted: #718096;
	$line: #e2e8f0;
	$surface: #f7fafc;
	$mine: #e2e8f0;
	$shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);

	.chat-window {
		display: flex;
		flex-direction: column;
		flex: none;
		width: 320px;
		max-width: calc(100vw - 16px);
		margin-bottom: 16px;
		color: $ink;
		border-radius: 8px;
		box-shadow: $shadow;
	}

	.chat-window:not(.chat-window--minimized) {
		height: 500px;
		max-height: calc(100vh - 96px);
		overflow: hidden;
		background: var(--white);
	}

	.chat-window__bar {
		display: flex;
		align-items: center;
		gap: 10px;
		height: 60px;
		padding: 0 8px 0 12px;
		color: var(--white);
		background: $chat-green;
		border-radius: 8px;
		cursor: pointer;
		outline: none;

		&:focus-visible {
			box-shadow: 0 0 0 2px var(--white) inset, $shadow;
		}
	}

	.chat-window__bar--unread {
		animation: chat-window-pulse 2s infinite;
	}

	.chat-window__bar-badge {
		min-width: 24px;
		height: 24px;
		padding: 0 6px;
		font-size: 12px;
		font-weight: 800;
		line-height: 24px;
		text-align: center;
		color: var(--success);
		background: var(--white);
		border-radius: 9999px;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
		font-variant-numeric: tabular-nums;
	}

	.chat-window__header {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		gap: 10px;
		height: 60px;
		padding: 0 8px 0 12px;
		color: var(--white);
		background: $chat-green;
	}

	.chat-window__heading {
		display: flex;
		flex: 1;
		flex-direction: column;
		min-width: 0;
		line-height: 1.25;
	}

	.chat-window__title {
		overflow: hidden;
		font-size: 14px;
		font-weight: 700;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.chat-window__subtitle {
		overflow: hidden;
		font-size: 11px;
		font-weight: 600;
		white-space: nowrap;
		text-overflow: ellipsis;
		opacity: 0.9;
	}

	.chat-window__icon-button {
		display: flex;
		flex-shrink: 0;
		padding: 5px;
		color: var(--white);
		background: transparent;
		border: 0;
		border-radius: 9999px;
		cursor: pointer;

		&:hover {
			background: rgba(255, 255, 255, 0.2);
		}
	}

	.chat-window__info {
		display: flex;
		flex-shrink: 0;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
		min-height: 40px;
		padding: 6px 12px;
		background: #edf2f7;
		border-bottom: 1px solid $line;
	}

	.chat-window__order-link {
		display: inline-flex;
		align-items: center;
		gap: 4px;
		min-width: 0;
		padding: 3px 6px 3px 8px;
		font-size: 12px;
		font-weight: 700;
		color: $chat-green-dark;
		background: var(--white);
		border: 1px solid $line;
		border-radius: 6px;
		cursor: pointer;

		&:hover {
			border-color: #cbd5e0;
		}

		&:focus-visible {
			outline: 2px solid $chat-green;
			outline-offset: 1px;
		}
	}

	.chat-window__order-link-label {
		min-width: 0;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.chat-window__general {
		font-size: 12px;
		font-weight: 600;
		color: $muted;
	}

	.chat-window__end {
		display: inline-flex;
		flex-shrink: 0;
		align-items: center;
		gap: 4px;
		margin-left: auto;
		padding: 3px 10px;
		font-size: 12px;
		font-weight: 700;
		color: var(--error);
		background: rgba(204, 25, 25, 0.08);
		border: 1px solid rgba(204, 25, 25, 0.2);
		border-radius: 9999px;
		cursor: pointer;
	}

	.chat-window__confirm {
		flex-shrink: 0;
		padding: 10px 12px;
		font-size: 13px;
		font-weight: 600;
		background: #fff5f5;
		border-bottom: 1px solid #fed7d7;
	}

	.chat-window__confirm-actions {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
		margin-top: 8px;
	}

	.chat-window__ghost,
	.chat-window__danger {
		padding: 4px 12px;
		font-size: 12px;
		font-weight: 700;
		border-radius: 9999px;
		cursor: pointer;
	}

	.chat-window__ghost {
		color: $text;
		background: var(--white);
		border: 1px solid $line;
	}

	.chat-window__danger {
		color: var(--white);
		background: var(--error);
		border: 1px solid var(--error);
	}

	.chat-window__messages {
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: 8px;
		min-height: 0;
		padding: 12px;
		overflow-y: auto;
		background-color: var(--white);

		&::-webkit-scrollbar {
			width: 4px;
		}

		&::-webkit-scrollbar-thumb {
			background: #cbd5e0;
			border-radius: 6px;
		}
	}

	.chat-window__loading {
		display: flex;
		flex: 1;
		align-items: center;
		justify-content: center;
		font-size: 24px;
		color: $chat-green;
	}

	.chat-window__empty {
		align-self: center;
		margin: auto 0;
		padding: 10px 14px;
		font-size: 13px;
		color: $muted;
		background: $surface;
		border: 1px solid $line;
		border-radius: 12px;
	}

	.chat-window__day {
		display: flex;
		flex-shrink: 0;
		justify-content: center;

		span {
			padding: 2px 10px;
			font-size: 11px;
			font-weight: 600;
			color: $text;
			background: #edf2f7;
			border-radius: 9999px;
		}
	}

	.chat-bubble-row {
		display: flex;
		flex-shrink: 0;
		align-items: flex-start;
		gap: 6px;
	}

	.chat-bubble-row--mine {
		justify-content: flex-end;
	}

	.chat-bubble-row__avatar {
		margin-top: 2px;
	}

	.chat-bubble {
		display: flex;
		flex-direction: column;
		gap: 2px;
		max-width: 78%;
		padding: 6px 10px;
		font-size: 13px;
		line-height: 1.35;
		border-radius: 14px;
		box-shadow: 0 1px 1px rgba(0, 0, 0, 0.06);
	}

	.chat-bubble--mine {
		color: $ink;
		background: $mine;
		border-top-right-radius: 4px;
	}

	.chat-bubble--theirs {
		color: $ink;
		background: var(--white);
		border-top-left-radius: 4px;
	}

	.chat-bubble__name {
		overflow: hidden;
		font-size: 11px;
		font-weight: 700;
		white-space: nowrap;
		text-overflow: ellipsis;
	}

	.chat-bubble__photo {
		display: block;
		margin: 2px 0;
		padding: 0;
		background: $line;
		border: 0;
		border-radius: 10px;
		overflow: hidden;
		cursor: zoom-in;

		img {
			display: block;
			width: 200px;
			max-width: 100%;
			height: 160px;
			object-fit: cover;
		}
	}

	.chat-bubble__text {
		white-space: pre-wrap;
		word-break: break-word;
	}

	.chat-bubble__meta {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 3px;
		font-size: 10px;
		color: $muted;
		font-variant-numeric: tabular-nums;
	}

	.chat-bubble__tick--read {
		color: var(--success);
	}

	.chat-bubble--typing {
		flex-direction: row;
		align-items: center;
		gap: 4px;
		padding: 12px 14px;
	}

	.chat-bubble__dot {
		width: 6px;
		height: 6px;
		background: $muted;
		border-radius: 9999px;
		animation: chat-window-bounce 1.1s infinite ease-in-out;

		&:nth-child(2) {
			animation-delay: 0.15s;
		}

		&:nth-child(3) {
			animation-delay: 0.3s;
		}
	}

	@keyframes chat-window-pulse {
		0%,
		100% {
			background-color: $chat-green;
		}
		50% {
			background-color: $chat-green-dark;
		}
	}

	@keyframes chat-window-bounce {
		0%,
		80%,
		100% {
			transform: translateY(0);
			opacity: 0.5;
		}
		40% {
			transform: translateY(-3px);
			opacity: 1;
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.chat-window__bar--unread,
		.chat-bubble__dot {
			animation: none;
		}
	}
</style>
